'use client';

import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { Box, Collapse, Paper } from '@mui/material';
import { DataGridPremium as DataGrid, GridCellParams, useGridApiRef } from '@mui/x-data-grid-premium';
import { useMutation, useQuery } from '@parkchirp/api-client';
import { useRouter } from 'next/router';
import React, { Dispatch, FC, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { CREATE_LEASING_COUNT_AND_RATES, DELETE_COUNT_AND_RATE, UPDATE_COUNT_AND_RATE } from '../../../mutations';
import { GET_RATES_BY_SUBSCRIPTION, GET_SUBLOTS } from '../../../queries';
import { SetShowDialog, SetShowSnackbar } from '../../../types';
import { CountAndRate, LeasingRight } from '../../../types/leases';
import {
	DefaultEditModes,
	dayjs,
	getAllowRowEdits,
	getGridRows,
	getTableLatestEndDate,
	leasingRightErrorMessage,
	stopAllEdits,
	validateLeasingCounts,
} from '../../../utils';
import { ClickablePaper } from '../../clickablePaper';
import { ConfirmationInputModal } from '../../modals';
import { ConversionRights } from './conversionRights';
import { getLeaseCountAndRateColumns, getRateAndSublotFields, getTableButtons } from './leaseDetails';

type LeaseCountAndRateProps = {
	countAndRate: CountAndRate;
	leasingRight: LeasingRight;
	refetchLeasingRight: () => Promise<any>;
	handleCancelNewMode: () => void;
	editMode: boolean;
	setEditMode: (x: boolean) => void;
	pageEditInProgress: boolean;
	conversionRightEditModes: DefaultEditModes;
	setConversionRightEditModes: (x: (y: DefaultEditModes) => DefaultEditModes) => void;
	setChildrenGridApiRef: any;
	rateId: string;
	setRateId: Dispatch<SetStateAction<string>>;
	sublotId: number;
	setSublotId: Dispatch<SetStateAction<number>>;
	leaseNewMode: boolean;
	setShowDialog: SetShowDialog;
	setShowSnackbar: SetShowSnackbar;
};

export const LeaseCountAndRate: FC<LeaseCountAndRateProps> = ({
	countAndRate: countAndRateProp,
	leasingRight,
	refetchLeasingRight,
	handleCancelNewMode,
	editMode,
	setEditMode,
	pageEditInProgress,
	conversionRightEditModes,
	setConversionRightEditModes,
	setChildrenGridApiRef,
	rateId,
	setRateId,
	sublotId,
	setSublotId,
	leaseNewMode,
	setShowDialog,
	setShowSnackbar,
}) => {
	const gridApiRef = useGridApiRef();
	const router = useRouter();

	const [rows, setRows] = useState(countAndRateProp.history);
	const [expanded, setExpanded] = useState(true);

	const newMode = useMemo(() => String(countAndRateProp.id).includes('new'), [countAndRateProp.id]);

	const columns = useMemo(
		() =>
			getLeaseCountAndRateColumns(
				gridApiRef,
				countAndRateProp,
				leasingRight.facilityTimezone,
				newMode,
				leasingRight.subscriptionHasInvoice,
			),
		[countAndRateProp, gridApiRef, leasingRight.facilityTimezone, leasingRight.subscriptionHasInvoice, newMode],
	);

	const allowEdit = editMode || !pageEditInProgress;
	const allowDelete = !newMode && !pageEditInProgress;

	const { data: ratesData } = useQuery(GET_RATES_BY_SUBSCRIPTION, {
		variables: { subscriptionId: leasingRight.subscriptionId },
		context: {
			apiVersion: 'v2',
			appName: process.env.NEXT_PUBLIC_APP_NAME,
		},
		skip: !newMode,
	});
	const rateOptions = ratesData?.getRatesBySubscription ?? [];

	const { data: sublotsData } = useQuery(GET_SUBLOTS, {
		variables: {
			facilityId: leasingRight.facilityId,
			includePrimary: true,
		},
		context: {
			apiVersion: 'v2',
			appName: process.env.NEXT_PUBLIC_APP_NAME,
		},
		skip: !newMode,
		onCompleted: (data) => {
			const mainFacility = data?.getSublots?.find((sublot) => sublot.name === 'Main Facility');
			if (mainFacility) {
				setSublotId(mainFacility.id);
			}
		},
	});
	const sublotOptions = sublotsData?.getSublots ?? [];

	const [createCountAndRate, { loading: createInProgress }] = useMutation(CREATE_LEASING_COUNT_AND_RATES);
	const [updateCountAndRate, { loading: updateInProgress }] = useMutation(UPDATE_COUNT_AND_RATE);
	const [deleteCountAndRate, { loading: deleteInProgress }] = useMutation(DELETE_COUNT_AND_RATE);

	const saveInProgress = createInProgress || updateInProgress || deleteInProgress;

	useEffect(() => {
		setRows(structuredClone(countAndRateProp.history));
	}, [countAndRateProp]);

	useEffect(() => {
		setChildrenGridApiRef((prev) => ({ ...prev, [countAndRateProp.id]: gridApiRef }));
	}, [countAndRateProp.id, gridApiRef, setChildrenGridApiRef]);

	/**
	 * This useEffect is needed as a workaround because for some reason
	 * isCellEditable() doesn't get the updated editMode state until clicking on the data grid
	 */
	useEffect(() => {
		if (editMode) {
			gridApiRef.current.setCellFocus(countAndRateProp.history[0].id, 'startDate');
		}
	}, [countAndRateProp.history, editMode, gridApiRef]);

	const deleteCountAndRateHandler = async () => {
		try {
			const response = await deleteCountAndRate({
				context: { apiVersion: 'v2' },
				variables: { id: countAndRateProp.id },
			});

			if (response?.data?.deleteCountAndRate?.leaseHasMoreCountAndRates === false) {
				router.push('subscriptions/leasing-rights/');
			} else {
				await refetchLeasingRight();
			}

			setShowSnackbar({
				message: 'Successfully deleted Count & Rate',
				severity: 'success',
				autoHideDuration: 5000,
			});
		} catch (error) {
			setShowSnackbar({
				message: error.message,
				severity: 'error',
				autoHideDuration: 8000,
			});
		} finally {
			setShowDialog(null);
		}
	};

	const handleDelete = () => {
		const dialogText = `Are you sure you want to delete this Count & Rate?
            ${leasingRight.countAndRates.length === 1 ? ' This will also delete the lease.' : ''}`;

		setShowDialog(
			<ConfirmationInputModal
				title={dialogText}
				validationString='DELETE'
				dismissText='Cancel'
				confirmText='Confirm'
				handleDismiss={() => setShowDialog(null)}
				handleConfirm={() => {
					deleteCountAndRateHandler();
				}}
			/>,
		);
	};

	const handleCancel = useCallback(() => {
		stopAllEdits({
			saveChanges: false,
			gridApiRef,
			columns,
		});
		setEditMode(false);

		if (newMode) handleCancelNewMode();
		else setRows(structuredClone(countAndRateProp.history));
	}, [columns, countAndRateProp.history, gridApiRef, handleCancelNewMode, newMode, setEditMode]);

	const handleAddRow = useCallback(() => {
		const latestEndDate = getTableLatestEndDate(gridApiRef);

		gridApiRef.current.updateRows([
			{
				id: `new-${uuidv4()}`,
				startDate: latestEndDate.add(1, 'day').format('YYYY-MM-DD'),
				endDate: leasingRight.expirationDate,
				minCount: 0,
				maxCount: null,
				enforceMaxCount: false,
				rightToConvert: false,
			},
		]);
	}, [gridApiRef, leasingRight.expirationDate]);

	const handleSave = useCallback(async () => {
		stopAllEdits({
			saveChanges: true,
			gridApiRef,
			columns,
		});

		const dataRows = getGridRows(gridApiRef);
		const updatedHistory = dataRows.map((row) => ({
			...row,
			id: String(row.id).includes('new') ? null : row.id,
		}));

		try {
			const invalidCounts = validateLeasingCounts({ leasingCountAndRateHistory: updatedHistory });
			if (invalidCounts) throw new Error(invalidCounts);

			try {
				if (newMode) {
					if (!rateId) throw new Error('Please select a rate');
					if (!sublotId) throw new Error('Please select a sublot');

					await createCountAndRate({
						variables: {
							leasingRightId: leasingRight.id,
							leasingCountAndRates: [
								{
									rateId,
									sublotId,
									leasingCountAndRateHistory: updatedHistory,
								},
							],
						},
						context: { apiVersion: 'v2' },
					});
				} else {
					await updateCountAndRate({
						variables: {
							id: countAndRateProp.id,
							history: updatedHistory,
							leasingRightId: leasingRight.id,
						},
						context: { apiVersion: 'v2' },
					});
				}
			} catch (e) {
				console.log(e);
				throw new Error(leasingRightErrorMessage(e));
			}

			setShowSnackbar({
				message: 'Saved successfully',
				severity: 'success',
				autoHideDuration: 5000,
			});

			setEditMode(false);
			await refetchLeasingRight();
		} catch (e) {
			setShowSnackbar({
				message: e.message,
				severity: 'error',
				autoHideDuration: 8000,
			});
		}
	}, [
		columns,
		countAndRateProp.id,
		createCountAndRate,
		gridApiRef,
		leasingRight.id,
		newMode,
		rateId,
		refetchLeasingRight,
		setEditMode,
		setShowSnackbar,
		sublotId,
		updateCountAndRate,
	]);

	const isCellEditable = useCallback(
		({ field, id, row }: GridCellParams) => {
			if (newMode && field !== 'enforceMaxCount') return true;
			if (!editMode) return false;

			const { facilityTimezone, subscriptionHasInvoice } = leasingRight;
			const uneditedHistory = countAndRateProp.history;

			const allowRowEdits = getAllowRowEdits(
				row,
				countAndRateProp.history,
				facilityTimezone,
				newMode,
				subscriptionHasInvoice,
			);

			switch (field) {
				case 'endDate': {
					const now = dayjs().tz(facilityTimezone);
					const endDateIsPast = dayjs(row.endDate).tz(facilityTimezone, true).isBefore(now, 'day');
					const endDateIsEdited = uneditedHistory.find((h) => h.id === id)?.endDate !== row.endDate;
					const allowEndDateEdit = endDateIsEdited || !endDateIsPast || !subscriptionHasInvoice;

					return allowEndDateEdit;
				}
				case 'enforceMaxCount':
					return allowRowEdits && !!row.maxCount;
				case 'startDate':
					return allowRowEdits;
				default:
					return allowRowEdits;
			}
		},
		[countAndRateProp, editMode, leasingRight, newMode],
	);

	return (
		<Box key={countAndRateProp.id}>
			<ClickablePaper
				onClick={() => setExpanded(!expanded)}
				disableClick={newMode}
				sx={{ backgroundColor: 'lightgray', paddingTop: '1em', paddingBottom: '1em' }}
			>
				<Box display='flex' alignItems='center' gap='1em' ml='1em'>
					{expanded ? <ExpandLess fontSize='large' /> : <ExpandMore fontSize='large' />}
					{getRateAndSublotFields({
						rateId,
						setRateId,
						sublotId,
						setSublotId,
						rateName: countAndRateProp.rateName,
						sublotName: countAndRateProp.sublotName,
						rateOptions,
						sublotOptions,
						newMode,
						gridApiRef,
						grossRatePrice: countAndRateProp.grossRatePrice,
					})}
				</Box>
			</ClickablePaper>
			<Collapse in={expanded}>
				<Paper
					sx={{
						backgroundColor: 'lightgray',
						borderTop: 'solid',
						borderColor: 'gray',
						paddingBottom: '0.5em',
					}}
				>
					{getTableButtons({
						newMode,
						leaseNewMode,
						editMode,
						setEditMode,
						allowEdit,
						allowDelete,
						saveInProgress,
						deleteInProgress,
						handleSave,
						handleAddRow,
						handleCancel,
						handleDelete,
					})}
					<Box
						sx={{
							backgroundColor: 'white',
							margin: '1em',
							...(editMode && {
								'& .MuiDataGrid-cell': { backgroundColor: 'gainsboro' },
								'& .MuiDataGrid-cell--actions': { backgroundColor: 'white' },
								'& .MuiDataGrid-cell--editable': { backgroundColor: 'white' },
							}),
						}}
					>
						<DataGrid
							rows={rows}
							columns={columns}
							apiRef={gridApiRef}
							isCellEditable={isCellEditable}
							columnVisibilityModel={{ actions: editMode }}
							autoHeight
							disableColumnReorder
							disableColumnMenu
							disableRowSelectionOnClick
							hideFooter
						/>
					</Box>
					<ConversionRights
						countAndRate={countAndRateProp}
						leasingRight={leasingRight}
						refetchLeasingRight={refetchLeasingRight}
						countAndRateId={countAndRateProp.id}
						pageEditInProgress={pageEditInProgress}
						conversionRightEditModes={conversionRightEditModes}
						setConversionRightEditModes={setConversionRightEditModes}
						setShowDialog={setShowDialog}
						setShowSnackbar={setShowSnackbar}
					/>
				</Paper>
			</Collapse>
		</Box>
	);
};
